import {
    DefaultApi,
    Configuration,
    RequiredError,
    ResponseError,
    StartLoginRequest,
    LoginPasswordRequest,
    VerifyTotpRequest,
    SetPasswordRequest,
    CreateTotpRequest,
    CreateWebauthnRequest,
} from "src/api/generated/frontend_public_v1";
import { ERROR_STORE } from "src/context/error-context";
import Console from "src/utils/console";

/** Hyphen separated uuid */
export type UUID = string;

const configuration = new Configuration({
    basePath: window.location.origin,
});

const defaultApi = new DefaultApi(configuration);

export const ApiPublic = {
    /** Authentication for the vpn access */
    vpnAuth: {
        getLogins: () => handleError(defaultApi.getVpnLogins()),
        startLogin: (request: StartLoginRequest) => handleError(defaultApi.startLogin({ StartLoginRequest: request })),
        loginWebauthn: () => handleError(defaultApi.loginWebauthn()),
        loginPassword: (request: LoginPasswordRequest) =>
            handleError(defaultApi.loginPassword({ LoginPasswordRequest: request })),
        verifyTotp: (request: VerifyTotpRequest) => handleError(defaultApi.verifyTotp({ VerifyTotpRequest: request })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        completeWebauthn: (body: any) => handleError(defaultApi.completeAuthWebauthn({ body })),
        logout: () => handleError(defaultApi.logoutUser()),
    },
    /** Modifies a local user's credentials*/
    credentials: {
        startInvitation: (invitationUuid: UUID) =>
            handleError(defaultApi.startInvitation({ link_uuid: invitationUuid })),
        startPostAuthReset: () => handleError(defaultApi.startPostAuthReset()),
        setPassword: (request: SetPasswordRequest) =>
            handleError(defaultApi.setPassword({ SetPasswordRequest: request })),
        deletePassword: () => handleError(defaultApi.deletePassword()),
        createTotpKey: (request: CreateTotpRequest) =>
            handleError(defaultApi.createTotpKey({ CreateTotpRequest: request })),
        deleteTotpKey: (keyUuid: UUID) => handleError(defaultApi.deleteTotpKey({ uuid: keyUuid })),
        createWebauthnKey: (request: CreateWebauthnRequest) =>
            handleError(defaultApi.createWebauthnKey({ CreateWebauthnRequest: request })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        completeWebauthn: (body: any) => handleError(defaultApi.completeWebauthn({ body })),
        deleteWebauthnKey: (keyUuid: UUID) => handleError(defaultApi.deleteWebauthnKey({ uuid: keyUuid })),
        submit: () => handleError(defaultApi.submitCredentials()),
    },
};

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            if (e.response.status === 401) {
                msg = e.response.statusText;
            } else {
                try {
                    const err = await e.response.json();
                    msg = `${e.response.statusText}. TraceId: ${err.trace_id}`;
                } catch {
                    Console.error("Got invalid json", e.response.body);
                    msg = `${e.response.statusText}. The server's response was invalid json.`;
                }
            }
        } else if (e instanceof RequiredError) {
            Console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            Console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}
